import { Dispatch, SetStateAction, forwardRef, memo, useImperativeHandle, useMemo } from 'react';
import { Grid } from '@mantine/core';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState
} from 'mantine-react-table';
import classes from './index.module.css';

const Table = forwardRef(
  (
    {
      tableData = [],
      tableColumns = [],
      isError = false,
      paginationIsLoading = false,
      numPages = 0,
      totalRowsCount = 0,
      pagination,
      setPagination,
      overrideTableConfigs = {
        state: {}
      },
      enableRowSelection = false,
    }: {
      tableData: any[];
      tableColumns: MRT_ColumnDef<any>[];
      isError?: boolean;
      paginationIsLoading?: boolean;
      numPages: number;
      totalRowsCount: number;
      overrideTableConfigs?: any;
      pagination: MRT_PaginationState;
      setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
      enableRowSelection?: boolean;
    },
    ref
  ) => {
    const columns = useMemo<MRT_ColumnDef<any>[]>(() => tableColumns, [tableColumns]);
    const pages = useMemo(() => numPages, [numPages]);
    const totalRowsCountMemo = useMemo(() => totalRowsCount, [totalRowsCount]);
    const { state: overrideState, ...restOverride } = overrideTableConfigs;
    const defaultTableConfig = {
      columns,
      data: tableData,
      enableRowSelection: enableRowSelection,
      mantineToolbarAlertBannerProps: isError
        ? {
            color: 'red',
            children: 'Error cargando información'
          }
        : undefined,
      enableColumnActions: false,
      enableColumnFilters: false,
      enablePagination: true,
      enableSorting: false,
      enableGlobalFilter: false,
      enableDensityToggle: false,
      enableFullScreenToggle: false,
      manualSorting: true,
      mantinePaginationProps: {
        showRowsPerPage: false
      },
      paginationDisplayMode: 'pages',
      state: {
        ...(overrideState || {}),
        pagination,
        isLoading: paginationIsLoading
      }, //pass the pagination state to the table
      manualPagination: true,
      pageCount: pages,
      rowCount: totalRowsCountMemo,
      onPaginationChange: setPagination,
      mantineTableProps: {
        highlightOnHover: false,
        striped: 'odd',
        withColumnBorders: true,
        withRowBorders: true,
        withTableBorder: true
      },
      ...restOverride,
    };

    const table = useMantineReactTable(defaultTableConfig);
    // Expose `table` to parent through ref
    useImperativeHandle(ref, () => ({
      getTableInstance: () => table
    }));
    return (
      <>
        <Grid gutter="md">
          <Grid.Col span={12} className={classes.tableRoot}>
            <MantineReactTable table={table} />
          </Grid.Col>
        </Grid>
      </>
    );
  }
);

export default memo(Table);
